import { useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '@/store/hooks';
import { getPublishedStatus, selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import FaviconUploader from '../../controls/favicon-uploader';
import { ISiteDetailMetadata } from '../../settings.types';
import TextArea from '@ui/text-area/text-area';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

interface SiteDetailProps {
  metadata: ISiteDetailMetadata;
  setMetaData: React.Dispatch<React.SetStateAction<{ name: string; description: string }>>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
}

const SiteDetail = ({
  metadata,
  setMetaData,
  isEditing,
  setPreviewModalOpen,
  onSave,
  onCancel,
  saveDisabled,
}: SiteDetailProps) => {
  const publishedStatus = useAppSelector(getPublishedStatus);
  const isSaving = useAppSelector(selectIsSavingWorkingProject);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMetaData((prev) => ({ ...prev, name: e.target.value }));
    },
    [setMetaData]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMetaData((prev) => ({ ...prev, description: e.target.value }));
    },
    [setMetaData]
  );

  const togglePreviewModal = () => setPreviewModalOpen((prev) => !prev);

  useEffect(() => {
    if (inputRef.current && isEditing) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  return (
    <Container
      className="flex padding-20 gap-16 overflow-hidden flex-column"
      borderRadius="12px"
      background={!isEditing ? 'var(--gray-900)' : 'var(--gray-800)'}
    >
      <div className="height-100-percent width-100-percent flex-column gap-16">
        <div className="flex ai-center jc-space-between gap-16">
          <div className="flex jc-center" style={{ alignSelf: 'flex-start' }}>
            <FaviconUploader />
          </div>
          <InputWithOverlay
            designSize="small"
            inputRef={inputRef}
            wrapperClass="flex-1"
            background="transparent"
            typography="H4"
            design={null}
            isBorderless
            inputProps={{
              value: metadata.name,
              className: 'padding-2 radius-4',
              onChange: handleNameChange,
            }}
          />
        </div>
        <div className="flex-column ai-flex-start gap-4">
          <OptionalComponent display={isEditing}>
            <Text className="L2" color="var(--gray-500)">
              Meta description
            </Text>
          </OptionalComponent>
          <div className="width-100-percent">
            <TextArea
              label="Meta description"
              value={metadata.description}
              className="P2"
              onChange={handleDescriptionChange}
            />
          </div>
        </div>
      </div>
      <div
        className="flex ai-center jc-space-between"
        style={{
          ...(isEditing && { zIndex: '10' }),
        }}
      >
        <Button
          backgroundColor="var(--gray-700)"
          hoverBackgroundColor="var(--gray-600)"
          height="32px"
          className="L2 padding-v-8 padding-h-16"
          onClick={togglePreviewModal}
          disabled={!publishedStatus?.url}
          disabledOpacity={0.6}
        >
          Preview
        </Button>

        <div className="flex ai-center gap-8 flex-1 jc-flex-end">
          <Button
            backgroundColor="var(--gray-700)"
            hoverBackgroundColor="var(--gray-600)"
            height="32px"
            className="L2 padding-v-8 padding-h-16"
            onClick={onCancel}
            disabled={isSaving || saveDisabled}
            disabledOpacity={0.6}
          >
            <Text className="L2">Cancel</Text>
          </Button>
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            padding="8px 16px"
            width="auto"
            onClick={onSave}
            disabled={isSaving || saveDisabled}
            disabledOpacity={0.6}
            hoverOpacity={0.8}
          >
            <Text color={getThemeColorContrastCSSVariable()} className="L2">
              Save
            </Text>
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default SiteDetail;
